import React, { useCallback, useMemo } from 'react';
import {
  chakra,
  Grid,
  GridItem,
  Box,
  Button,
  Flex,
  HStack,
  Select,
  Text,
  Tooltip as ChakraTooltip,
  IconButton,
  Spinner,
  Switch,
} from '@chakra-ui/react';
import { FaChevronDown } from 'react-icons/fa';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  LabelList,
  Bar,
  BarChart,
  ReferenceLine,
} from 'recharts';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { enGB } from 'date-fns/locale';
import { formatCurrency } from 'utils/CommonFunction';
import { sideBarRoutes } from 'utils/StaticData';
import { customStaticRanges } from 'utils/StaticData';
import PropTypes from 'prop-types';
import useRegions from 'hooks/useRegions';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import MerchantRevenueStationGraph from './MerchantRevenueStationGraph';
import { formatDate } from 'utils/CommonFunction';
import {
  processMerchantData,
  calculateCommissionAmount,
  getCommissionRate,
} from 'utils/chartUtils';
import useMerchantGroups from 'hooks/useMerchantGroups';
import useMerchantLabelFilter from 'hooks/useMerchantLabelFilter';
import useMerchantFilter from 'hooks/useMerchantFilter';
import GroupManagementModal from 'components/GroupManagementModal';
import MerchantFilterDropdown from 'components/MerchantFilterDropdown';
import {
  CustomTooltipForGrossRevenue,
  CustomTooltipForPowerbankRented,
  CustomTooltipForRecurring,
} from 'components/ChartTooltips';

const SummaryStat = ({ label, value, accent, withDivider }) => (
  <Box
    flex="1"
    textAlign="center"
    borderLeft={withDivider ? '1px solid #EAEAEA' : undefined}
  >
    <Text
      fontSize={{ base: 'sm', md: 'md' }}
      fontWeight="bold"
      color={accent || '#292D32'}
    >
      {value}
    </Text>
    <Text fontSize="xs" color="gray.500">
      {label}
    </Text>
  </Box>
);

SummaryStat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  accent: PropTypes.string,
  withDivider: PropTypes.bool,
};

// Footer under each chart: % change vs previous, total this period, total previous
const ChartSummaryFooter = ({ totals, formatValue }) => {
  const { thisPeriod, prevPeriod, change } = totals;
  const hasChange = change !== null && Number.isFinite(change);
  return (
    <Flex
      mt="5"
      align="center"
      minH="84px"
      border="1px solid #D9D9D9"
      borderRadius="16px"
      py="4"
    >
      <SummaryStat
        label="vs previous period"
        accent={
          hasChange ? (change >= 0 ? 'green.500' : 'red.500') : 'gray.400'
        }
        value={
          hasChange
            ? `${change >= 0 ? '↑' : '↓'} ${Math.abs(change).toFixed(1)}%`
            : 'N/A'
        }
      />
      <SummaryStat
        label="Total this period"
        value={formatValue(thisPeriod)}
        withDivider
      />
      <SummaryStat
        label="Total previous period"
        value={formatValue(prevPeriod)}
        withDivider
      />
    </Flex>
  );
};

ChartSummaryFooter.propTypes = {
  totals: PropTypes.shape({
    thisPeriod: PropTypes.number,
    prevPeriod: PropTypes.number,
    change: PropTypes.number,
  }).isRequired,
  formatValue: PropTypes.func.isRequired,
};

const RevenueOverViewGraph = ({
  adminDetails,
  handleSelectChange,
  selectedMerchant,
  currency,
  merchantNameList,
  revenueOverviewData,
  sellerCommissionData,
  isSellerCommissionLoading,
  isRevenueOverviewLoading,
  isErrorLoading,
  showCalendar,
  setShowCalendar,
  tempDateRange,
  dateRange,
  calendarFilterTitle,
  handleApply,
  handleClear,
  handleSelect,
  selectedRegion,
  handleRegionSelect,
  showTooltip,
  setShowTooltip,
  recurringUsers,
  isRecurringLoading,
  showAverage,
  setShowAverage,
  powerbankShowAvg,
  setPowerbankShowAvg,
}) => {
  // Initialize custom hooks
  const { regions } = useRegions();
  const merchantGroupsHook = useMerchantGroups();
  const merchantLabelsHook = useMerchantLabelFilter();
  const merchantFilterHook = useMerchantFilter(
    selectedMerchant,
    handleSelectChange,
  );

  // Fetch merchant-specific groups on mount
  React.useEffect(() => {
    merchantGroupsHook.fetchGroups('dashboard');
  }, [merchantGroupsHook.fetchGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch labels on mount. The backend scopes both the list and each label's
  // merchantCount to the caller's role, so no filtering is needed here.
  React.useEffect(() => {
    merchantLabelsHook.fetchLabels();
  }, [merchantLabelsHook.fetchLabels]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if any API is loading to disable all filters
  const isAnyApiLoading =
    isRevenueOverviewLoading ||
    isRecurringLoading ||
    isErrorLoading ||
    isSellerCommissionLoading;

  const filteredMerchants = useMemo(
    () => merchantFilterHook.getFilteredMerchants(merchantNameList),
    [merchantFilterHook.searchTerm, merchantNameList], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const { grossRevenueCountAvg, grossRevenueAvg } = revenueOverviewData;

  const grossRevenueChartData = useMemo(
    () => processMerchantData(revenueOverviewData?.grossRevenue),
    [revenueOverviewData?.grossRevenue],
  );

  const grossRevenueCountChartData = useMemo(
    () => processMerchantData(revenueOverviewData?.grossRevenueCount),
    [revenueOverviewData?.grossRevenueCount],
  );

  const { merchantColors: globalMerchantColors } = grossRevenueChartData;

  const grossRevenue = useMemo(
    () =>
      (revenueOverviewData?.grossRevenueCount ?? []).map((item) => ({
        ...item,
        total: Number(item.total),
        prevTotal: Number(item.prevTotal),
      })),
    [revenueOverviewData?.grossRevenueCount],
  );

  // Sum a series' current/previous totals and the % change between them
  const summarizeTotals = (data) => {
    const list = Array.isArray(data) ? data : [];
    const thisPeriod = list.reduce((sum, i) => sum + Number(i.total || 0), 0);
    const prevPeriod = list.reduce(
      (sum, i) => sum + Number(i.prevTotal || 0),
      0,
    );
    const change = prevPeriod
      ? ((thisPeriod - prevPeriod) / prevPeriod) * 100
      : null;
    return { thisPeriod, prevPeriod, change };
  };

  const grossRevenueTotals = useMemo(
    () => summarizeTotals(revenueOverviewData?.grossRevenue),
    [revenueOverviewData?.grossRevenue],
  );

  const powerbankTotals = useMemo(
    () => summarizeTotals(revenueOverviewData?.grossRevenueCount),
    [revenueOverviewData?.grossRevenueCount],
  );

  const breakdownData = useMemo(() => {
    const all = revenueOverviewData?.breakdown || [];
    const refund = all.find((item) => item.label === 'Refund')?.value || 0;
    // VAT label is dynamic (e.g. "VAT " or "VAT (21%)"), so match by prefix.
    const vat = all.find((item) => item?.label?.startsWith('VAT'))?.value || 0;
    // Costs (Transaction costs, Lost powerbank costs) — everything but Refund and VAT.
    const costBreakdowns = all.filter(
      (item) => item.label !== 'Refund' && !item?.label?.startsWith('VAT'),
    );
    // totalRevenue is captured − refund, so Gross (captured) adds the refund back.
    const grossRevenue = (revenueOverviewData?.totalRevenue || 0) + refund;
    // Net Revenue = Gross − Refund − VAT (backend already provides this value).
    const netRevenue = (revenueOverviewData?.totalRevenue || 0) - vat;
    const commissionableRevenue =
      netRevenue -
      costBreakdowns.reduce((total, item) => total + (item?.value || 0), 0);
    return {
      grossRevenue,
      refund,
      vat,
      netRevenue,
      costBreakdowns,
      commissionableRevenue,
    };
  }, [revenueOverviewData?.breakdown, revenueOverviewData?.totalRevenue]);

  const handleClearSelection = useCallback(() => {
    merchantFilterHook.handleClearSelection();
    merchantGroupsHook.clearSelectedGroups();
    merchantLabelsHook.clearSelectedLabels();
  }, [merchantFilterHook, merchantGroupsHook, merchantLabelsHook]);

  const handleGroupSelection = useCallback(
    (group) =>
      merchantGroupsHook.handleGroupSelection(
        group,
        merchantFilterHook.pendingSelectedMerchant,
        merchantFilterHook.setPendingSelectedMerchant,
      ),
    [
      merchantGroupsHook,
      merchantFilterHook.pendingSelectedMerchant,
      merchantFilterHook.setPendingSelectedMerchant,
    ],
  );

  const handleLabelSelection = useCallback(
    (label) =>
      merchantLabelsHook.handleLabelSelection(
        label,
        merchantFilterHook.pendingSelectedMerchant,
        merchantFilterHook.setPendingSelectedMerchant,
        // The region-filtered list this dropdown is showing, not the label's
        // raw members - a chip must never select a merchant that is not on
        // screen (e.g. while a region is selected).
        merchantNameList,
      ),
    [
      merchantLabelsHook,
      merchantFilterHook.pendingSelectedMerchant,
      merchantFilterHook.setPendingSelectedMerchant,
      merchantNameList,
    ],
  );

  const handleCreateGroup = useCallback(
    () => merchantGroupsHook.openCreateGroupModal('dashboard'),
    [merchantGroupsHook],
  );

  const handleEditGroup = useCallback(
    (group) => merchantGroupsHook.openEditGroupModal(group, 'dashboard'),
    [merchantGroupsHook],
  );

  return (
    <>
      {/* Revenue Overview */}
      <Box>
        {/* Header and Filters in One Row */}
        <Flex justify="space-between" align="center" flexWrap="wrap" gap="20px">
          <Text fontSize="2xl" fontWeight="semibold">
            Revenue Overview
          </Text>

          {/* Filters */}
          <HStack
            spacing="0"
            gap="5"
            flexDirection={{ base: 'column', md: 'row' }}
            width={{ base: '100%', md: 'auto' }}
          >
            {/* region filter */}
            {adminDetails?.role === sideBarRoutes?.Admin && (
              <Select
                width={{ base: '100%', md: 'auto' }}
                placeholder="All regions"
                bg="white"
                borderColor="reviveGray"
                color="#292D32"
                fontWeight="semibold"
                borderRadius="8px"
                onChange={(e) => {
                  merchantFilterHook.setPendingSelectedMerchant([]);
                  handleRegionSelect(e);
                }}
                value={selectedRegion}
                isDisabled={isAnyApiLoading}
              >
                {regions?.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.label}
                  </option>
                ))}
              </Select>
            )}

            <MerchantFilterDropdown
              searchTerm={merchantFilterHook.searchTerm}
              setSearchTerm={merchantFilterHook.setSearchTerm}
              selectedMerchantText={merchantFilterHook.getSelectedMerchantText()}
              isPopoverOpen={merchantFilterHook.isMerchantPopoverOpen}
              openPopover={merchantFilterHook.openMerchantPopover}
              closePopover={merchantFilterHook.closeMerchantPopover}
              isDisabled={isAnyApiLoading}
              merchantNameList={merchantNameList}
              filteredMerchants={filteredMerchants}
              pendingSelectedMerchant={
                merchantFilterHook.pendingSelectedMerchant
              }
              onSelectAll={merchantFilterHook.handleSelectAll}
              onMerchantToggle={merchantFilterHook.handleMerchantToggle}
              isSelectAllChecked={merchantFilterHook.isSelectAllChecked}
              isMerchantChecked={merchantFilterHook.isMerchantChecked}
              onApply={merchantFilterHook.handleApplySelection}
              onCancel={merchantFilterHook.handleCancelSelection}
              onClearSelection={handleClearSelection}
              merchantGroups={merchantGroupsHook.merchantGroups}
              selectedGroupIds={merchantGroupsHook.selectedGroupIds}
              isGroupsLoading={merchantGroupsHook.isFetchingGroups}
              onGroupSelection={handleGroupSelection}
              onCreateGroup={handleCreateGroup}
              onEditGroup={handleEditGroup}
              showLabels
              merchantLabels={merchantLabelsHook.labels}
              selectedLabelIds={merchantLabelsHook.selectedLabelIds}
              isLabelsLoading={merchantLabelsHook.isFetchingLabels}
              onLabelSelection={handleLabelSelection}
            />

            <Box
              position="relative"
              flexShrink="0"
              width={{ base: '100%', md: 'auto' }}
            >
              <Button
                py="10px"
                px="15"
                fontWeight="semibold"
                type="button"
                bg="white"
                border="1px solid"
                borderColor="reviveGray"
                color="#292D32"
                borderRadius="8px"
                onClick={() => setShowCalendar(!showCalendar)}
                justifyContent="space-between"
                width={{ base: '100%', md: 'auto' }}
                isDisabled={isAnyApiLoading}
              >
                {calendarFilterTitle}{' '}
                <FaChevronDown size={11} style={{ marginLeft: '8px' }} />
              </Button>
              {/* Date Range Picker Dropdown */}
              {showCalendar && (
                <>
                  <Box
                    position="fixed"
                    bg="rgb(0 0 0 / 40%)"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    zIndex="9"
                    onClick={() => setShowCalendar(false)}
                  ></Box>
                  <Box
                    p="32px"
                    mt="8px"
                    position="absolute"
                    right="0"
                    zIndex="10"
                    bg="white"
                    boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
                    borderRadius="20px"
                    className="custom-date-picker"
                  >
                    <DateRangePicker
                      ranges={tempDateRange}
                      onChange={handleSelect}
                      showSelectionPreview={true}
                      moveRangeOnFirstSelection={false}
                      months={2}
                      direction="horizontal"
                      locale={enGB}
                      minDate={new Date(2000, 0, 1)}
                      maxDate={new Date()}
                      staticRanges={customStaticRanges}
                      inputRanges={[]}
                    />
                    <Flex justifyContent="right" gap="12px" marginTop="30px">
                      <Button
                        className="clear-btn"
                        border="1px solid"
                        borderColor="reviveGray"
                        borderRadius="10px"
                        onClick={handleClear}
                        isDisabled={isAnyApiLoading}
                      >
                        Clear
                      </Button>
                      <Button
                        className="apply-btn"
                        bg="#6658fd"
                        border="1px solid"
                        borderColor="#6658fd"
                        color="white"
                        borderRadius="10px"
                        _hover={{
                          bg: 'transparent',
                          color: '#6658fd',
                        }}
                        onClick={handleApply}
                        isDisabled={isAnyApiLoading}
                      >
                        Apply
                      </Button>
                    </Flex>
                  </Box>
                </>
              )}
            </Box>
          </HStack>
        </Flex>
      </Box>

      {/* Reference statement — above the cards so all three columns align below it */}
      <Flex
        bg="#EAF0FF"
        borderRadius="12px"
        p="3"
        align="center"
        gap="2"
        mt="6"
        w="fit-content"
        maxW="full"
      >
        <Box color="#3B5BDB" fontSize="22px" flexShrink="0">
          <IoMdInformationCircleOutline />
        </Box>
        <Text fontSize="sm" color="#292D32">
          This amount is for reference only, please treat the invoice as the
          standard.
        </Text>
      </Flex>

      {/* Charts */}
      <Grid
        templateColumns="repeat(12, 1fr)"
        mt="4"
        gap={{ base: '24px', md: '16px', lg: '24px' }}
      >
        <GridItem
          colSpan={{ base: '12', xl: '4' }}
          display="flex"
          flexDirection="column"
        >
          <Box
            w="full"
            flex="1"
            p={{ base: '4', md: '5' }}
            bg="white"
            border="1px solid #D9D9D9"
            borderRadius="20px"
            boxShadow="0px 0px 20px rgba(32, 57, 70, 0.06)"
          >
            {/* Selected Period Title */}
            <Text
              fontSize={{ base: '16px', md: '24px' }}
              fontWeight="semibold"
              mb="30px"
              color="primary"
              display="flex"
              flexWrap="wrap"
            >
              <chakra.span>Selected Period</chakra.span>
              <chakra.span opacity="0">0</chakra.span>
              <chakra.span>
                {`${formatDate(dateRange[0].startDate)} - ${formatDate(
                  dateRange[0].endDate,
                )}`}
              </chakra.span>
            </Text>

            {/* Data Rows */}
            {/* new code as per new UI  */}
            {(() => {
              const {
                grossRevenue,
                refund,
                vat,
                netRevenue,
                costBreakdowns,
                commissionableRevenue,
              } = breakdownData;

              return (
                <Box>
                  {/* Gross Revenue (captured, before refund) + Refund */}
                  <Flex justify="space-between" py="1">
                    <Text fontWeight="medium" color="#292D32">
                      Gross Revenue
                    </Text>
                    <Text fontSize="sm">
                      {formatCurrency(grossRevenue, currency)}
                    </Text>
                  </Flex>
                  <Flex justify="space-between" py="1">
                    <Text fontWeight="medium" color="#292D32">
                      Refund
                    </Text>
                    <Text fontSize="sm">
                      - {formatCurrency(refund, currency)}
                    </Text>
                  </Flex>
                  <Flex justify="space-between" py="1">
                    <Text fontWeight="medium" color="#292D32">
                      VAT
                    </Text>
                    <Text fontSize="sm">- {formatCurrency(vat, currency)}</Text>
                  </Flex>

                  <Box borderTop="1px solid" borderColor="#EAEAEA" my="3" />

                  {/* Net Revenue = Gross − Refund − VAT */}
                  <Flex justify="space-between" py="1">
                    <Text fontSize="md" fontWeight="bold" color="#292D32">
                      Net Revenue
                    </Text>
                    <Text fontSize="md" fontWeight="bold" color="#292D32">
                      {formatCurrency(netRevenue, currency)}
                    </Text>
                  </Flex>

                  <Box borderTop="1px solid" borderColor="#EAEAEA" my="3" />

                  {/* Costs (Transaction costs / Lost powerbank costs) */}
                  {costBreakdowns.map((item, index) => (
                    <Flex justify="space-between" py="1" key={index}>
                      <Text fontWeight="medium" color="#292D32">
                        {item.label}
                      </Text>
                      <Text fontSize="sm">
                        - {formatCurrency(item?.value, currency)}
                      </Text>
                    </Flex>
                  ))}

                  <Box borderTop="1px solid" borderColor="#EAEAEA" my="3" />

                  {/* Commissionable revenue */}
                  <Flex justify="space-between" py="1">
                    <Text fontSize="md" fontWeight="bold" color="#292D32">
                      Commissionable revenue
                    </Text>
                    <Text fontSize="md" fontWeight="bold" color="#292D32">
                      {formatCurrency(commissionableRevenue, currency)}
                    </Text>
                  </Flex>

                  {[
                    sideBarRoutes?.Seller,
                    sideBarRoutes?.Organization,
                  ].includes(adminDetails?.role) && (
                    <>
                      <Box borderTop="1px solid" borderColor="#EAEAEA" my="3" />
                      <Flex justify="space-between" py="1">
                        <Text fontWeight="medium" color="#292D32">
                          {`Commission (${getCommissionRate(sellerCommissionData, selectedMerchant, revenueOverviewData)}%)`}
                        </Text>
                        <Text fontSize="sm">
                          {isSellerCommissionLoading ? (
                            <Spinner size="sm" />
                          ) : (
                            formatCurrency(
                              calculateCommissionAmount(
                                sellerCommissionData,
                                selectedMerchant,
                                merchantNameList,
                                revenueOverviewData,
                              ),
                            )
                          )}
                        </Text>
                      </Flex>
                    </>
                  )}
                </Box>
              );
            })()}
          </Box>

          {/* Quantity / Lost powerbanks / Active Stations — outside the card */}
          <Flex
            mt="5"
            align="center"
            minH="84px"
            border="1px solid #D9D9D9"
            borderRadius="20px"
            bg="white"
            boxShadow="0px 0px 20px rgba(32, 57, 70, 0.06)"
            py="4"
          >
            <Box flex="1" textAlign="center">
              <Text fontSize="sm" color="gray.500">
                Quantity
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="#292D32">
                {revenueOverviewData.totalRented}
              </Text>
            </Box>
            {(adminDetails?.role === sideBarRoutes?.Admin ||
              adminDetails?.role === sideBarRoutes?.Dealer) && (
              <Box flex="1" textAlign="center" borderLeft="1px solid #EAEAEA">
                <Text fontSize="sm" color="gray.500">
                  Lost powerbanks
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="#292D32">
                  {revenueOverviewData.powerbankLostCount || 0}
                </Text>
              </Box>
            )}
            <Box flex="1" textAlign="center" borderLeft="1px solid #EAEAEA">
              <Text fontSize="sm" color="gray.500">
                Active Stations
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="#292D32">
                {revenueOverviewData.stationCount}
              </Text>
            </Box>
          </Flex>
        </GridItem>
        <GridItem
          colSpan={{ base: '12', md: '6', xl: '4' }}
          display="flex"
          flexDirection="column"
        >
          <Box
            w="full"
            flex="1"
            display="flex"
            flexDirection="column"
            p={{ base: '4', md: '5' }}
            bg="white"
            border="1px solid #D9D9D9"
            borderRadius="20px"
            boxShadow="0px 0px 20px rgba(32, 57, 70, 0.06)"
          >
            <Flex justify="space-between" align="center">
              <Text
                fontSize={{ base: '16px', md: '24px' }}
                fontWeight="semibold"
                mb="30px"
                color="primary"
              >
                Gross Revenue
              </Text>
              <Flex align="center" gap="2" mb="30px">
                <Switch
                  id="show-average"
                  colorScheme="teal"
                  isChecked={showAverage}
                  onChange={() => setShowAverage(!showAverage)}
                />
                <Text fontSize="sm">Average</Text>
              </Flex>
            </Flex>

            <Box flex="1" minH="300px">
              {isRevenueOverviewLoading ? (
                <Flex justify="center" alignItems="center" height="100%">
                  <Spinner size="lg" color="black.500" />
                </Flex>
              ) : (
                (() => {
                  const { processedData, merchantList, merchantColors } =
                    grossRevenueChartData;

                  // Use processed data if merchants exist, otherwise use original data
                  const chartData =
                    merchantList.length > 0
                      ? processedData
                      : revenueOverviewData?.grossRevenue;

                  return (
                    <ResponsiveContainer width={'100%'} height="100%">
                      <LineChart
                        data={chartData}
                        margin={{ top: 20, left: 0 }}
                        accessibilityLayer
                      >
                        <XAxis
                          dataKey="time"
                          padding={{ left: 30, right: 30 }}
                        />
                        <YAxis
                          width={80}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(value) =>
                            formatCurrency(value, currency)
                          }
                        />
                        <Tooltip
                          content={
                            <CustomTooltipForGrossRevenue
                              currency={currency}
                              showAverage={showAverage}
                              grossRevenueAvg={grossRevenueAvg}
                              globalMerchantColors={globalMerchantColors}
                            />
                          }
                        />
                        <CartesianGrid
                          strokeDasharray="3 3"
                          horizontal={true}
                          vertical={false}
                        />

                        {/* Conditional rendering based on merchant data */}
                        {merchantList.length > 0 ? (
                          // Show merchant-specific lines when merchants exist
                          merchantList.map((merchant) => (
                            <Line
                              key={merchant.merchantId}
                              type="monotone"
                              dataKey={`merchant_${merchant.merchantId}`}
                              stroke={merchantColors[merchant.merchantId]}
                              strokeWidth={2}
                              activeDot={{ r: 8 }}
                              name={merchant.name}
                              connectNulls={false}
                            />
                          ))
                        ) : (
                          // Show original total and previous lines when no merchants
                          <>
                            <Line
                              type="monotone"
                              dataKey="prevTotal"
                              stroke="#D3D3D3"
                              strokeWidth={2}
                              activeDot={{ r: 8 }}
                              name="Previous Period"
                            />
                            <Line
                              type="monotone"
                              dataKey="total"
                              stroke="#000000"
                              strokeWidth={2}
                              activeDot={{ r: 8 }}
                              name="Current Period Total"
                            />
                          </>
                        )}

                        {/* Average reference line */}
                        {showAverage && (
                          <ReferenceLine
                            y={grossRevenueAvg}
                            stroke="red"
                            strokeWidth={2}
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  );
                })()
              )}
            </Box>

            <ChartSummaryFooter
              totals={grossRevenueTotals}
              formatValue={(v) => formatCurrency(v, currency)}
            />
          </Box>
        </GridItem>
        <GridItem
          colSpan={{ base: '12', md: '6', xl: '4' }}
          display="flex"
          flexDirection="column"
        >
          <Box
            w="full"
            flex="1"
            display="flex"
            flexDirection="column"
            p={{ base: '4', md: '5' }}
            bg="white"
            border="1px solid #D9D9D9"
            borderRadius="20px"
            boxShadow="0px 0px 20px rgba(32, 57, 70, 0.06)"
          >
            <Flex justify="space-between" align="center">
              <Text
                fontSize={{ base: '16px', md: '24px' }}
                fontWeight="semibold"
                mb="30px"
                color="primary"
              >
                Powerbanks Rented
              </Text>
              <Flex align="center" gap="2" mb="30px">
                <Switch
                  id="show-average"
                  colorScheme="teal"
                  isChecked={powerbankShowAvg}
                  onChange={() => setPowerbankShowAvg(!powerbankShowAvg)}
                />
                <Text fontSize="sm">Average</Text>
              </Flex>
            </Flex>

            <Box flex="1" minH="300px">
              {isRevenueOverviewLoading ? (
                <Flex justify="center" alignItems="center" height="100%">
                  <Spinner size="lg" color="black.500" />
                </Flex>
              ) : (
                revenueOverviewData?.grossRevenueCount?.length > 0 &&
                (() => {
                  const { processedData, merchantList, merchantColors } =
                    grossRevenueCountChartData;

                  // Use processed data if merchants exist, otherwise use original grossRevenue
                  const chartData =
                    merchantList.length > 0 ? processedData : grossRevenue;

                  return (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={chartData}
                        margin={{ top: 30, right: 20, left: 0, bottom: 0 }}
                      >
                        <XAxis
                          dataKey="time"
                          padding={{ left: 30, right: 30 }}
                        />
                        <YAxis axisLine={false} tickLine={false} />
                        <Tooltip
                          content={
                            <CustomTooltipForPowerbankRented
                              powerbankShowAvg={powerbankShowAvg}
                              grossRevenueCountAvg={grossRevenueCountAvg}
                              globalMerchantColors={globalMerchantColors}
                            />
                          }
                        />
                        <CartesianGrid
                          strokeDasharray="3 3"
                          horizontal
                          vertical={false}
                        />
                        {(() => {
                          return merchantList.length > 0 ? (
                            // Show merchant-specific lines when merchants exist
                            merchantList.map((merchant) => (
                              <Line
                                key={merchant.merchantId}
                                type="monotone"
                                dataKey={`merchant_${merchant.merchantId}`}
                                stroke={merchantColors[merchant.merchantId]}
                                strokeWidth={2}
                                activeDot={{ r: 6 }}
                                name={merchant.name}
                                connectNulls={false}
                                dot
                                clipPath={undefined}
                              />
                            ))
                          ) : (
                            // Show original total and previous lines when no merchants
                            <>
                              <Line
                                type="monotone"
                                dataKey="prevTotal"
                                stroke="#D3D3D3"
                                strokeWidth={2}
                                dot
                                activeDot={{ r: 6 }}
                                clipPath={undefined}
                                name="Previous Period"
                              />
                              <Line
                                type="monotone"
                                dataKey="total"
                                stroke="#000000"
                                strokeWidth={2}
                                dot
                                activeDot={{ r: 6 }}
                                clipPath={undefined}
                                name="Current Period Total"
                              />
                            </>
                          );
                        })()}
                        {powerbankShowAvg && (
                          <ReferenceLine
                            y={grossRevenueCountAvg}
                            stroke="red"
                          />
                          // <Line
                          //   type="monotone"
                          //   dataKey="avg"
                          //   stroke="red"
                          //   strokeWidth={2}
                          //   activeDot={{ r: 8 }}
                          // ></Line>
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  );
                })()
              )}
            </Box>

            <ChartSummaryFooter
              totals={powerbankTotals}
              formatValue={(v) => Math.round(v).toLocaleString('en-US')}
            />
          </Box>
        </GridItem>
      </Grid>

      {/* Reoccurring Users */}
      <Grid
        templateColumns="repeat(12, 1fr)"
        mt="6"
        gap={{ base: '24px', md: '16px', lg: '24px' }}
      >
        {/* Revenue Per Merchant Graph with station Id  */}
        <GridItem colSpan={{ base: '12', xl: '8' }}>
          <Box
            w="full"
            h="full"
            p={{ base: '4', md: '5' }}
            bg="white"
            border="1px solid #D9D9D9"
            borderRadius="20px"
            boxShadow="0px 0px 20px rgba(32, 57, 70, 0.06)"
          >
            <Text
              fontSize={{ base: '16px', md: '24px' }}
              fontWeight="semibold"
              mb="30px"
              color="primary"
            >
              Gross Revenue Per Station
            </Text>
            <MerchantRevenueStationGraph
              dynamicData={recurringUsers?.revenuePerMerchant}
              isRecurringLoading={isRecurringLoading}
              currency={currency}
            />
          </Box>
        </GridItem>

        {/* Reoccurring Users Graph */}
        <GridItem colSpan={{ base: '12', xl: '4' }}>
          <Box
            w="full"
            h="full"
            p={{ base: '4', md: '5' }}
            bg="white"
            border="1px solid #D9D9D9"
            borderRadius="20px"
            boxShadow="0px 0px 20px rgba(32, 57, 70, 0.06)"
          >
            <Flex justifyContent="space-between" alignItems="center" mb="30px">
              <Text
                fontSize={{ base: '16px', md: '24px' }}
                fontWeight="semibold"
                color="primary"
              >
                Number of Reoccurring Users
              </Text>
              <ChakraTooltip
                label="The Graph Highlights the Number of Reoccurring Users. A Reoccurring User is Defined as the User, whose last 4 Digits of Card Occurred in the Dataset more than once."
                fontSize="sm"
                p="10px"
                marginRight="10px"
                borderRadius="10px"
                placement="bottom-end"
                isOpen={showTooltip}
                backgroundColor={'white'}
                color={'black'}
                filter="drop-shadow(2px 2px 6px rgba(0, 0, 0, 0.16))"
              >
                <IconButton
                  aria-label="Info"
                  icon={<IoMdInformationCircleOutline size={28} />}
                  variant="ghost"
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                />
              </ChakraTooltip>
            </Flex>
            {isRecurringLoading ? (
              <Flex justify="center" alignItems="center" height="300px">
                <Spinner size="lg" color="black.500" />
              </Flex>
            ) : recurringUsers?.recurringUsers?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={recurringUsers?.recurringUsers}
                  margin={{ top: 15, right: 10, left: 0, bottom: 50 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={true}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    interval={0}
                    tick={(props) => {
                      const { x, y, payload } = props;
                      const name = payload.value;
                      const maxLength = 10;
                      const truncatedName =
                        name.length > maxLength
                          ? `${name.substring(0, maxLength)}…`
                          : name;

                      return (
                        <text
                          x={x}
                          y={y + 10}
                          textAnchor={
                            recurringUsers?.recurringUsers?.length < 4
                              ? 'middle'
                              : 'end'
                          }
                          fontSize={12}
                          transform={
                            recurringUsers?.recurringUsers?.length < 4
                              ? undefined
                              : `rotate(-60, ${x}, ${y})`
                          }
                        >
                          {truncatedName}
                        </text>
                      );
                    }}
                  />
                  <YAxis
                    tickInterval={1}
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={
                      <CustomTooltipForRecurring
                        adminDetails={adminDetails}
                        sideBarRoutes={sideBarRoutes}
                      />
                    }
                  />

                  <Bar
                    dataKey="count"
                    fill="url(#colorGradient)"
                    radius={[5, 5, 0, 0]}
                  >
                    <LabelList
                      dataKey="count"
                      position="top"
                      fill="#000"
                      fontSize={14}
                    />
                  </Bar>
                  <defs>
                    <linearGradient
                      id="colorGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#00ff99" />
                      <stop offset="100%" stopColor="#00cc66" />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Flex justify="center" alignItems="center" height="300px">
                <Text fontSize="18px" fontWeight="bold" color="gray.500">
                  No records available
                </Text>
              </Flex>
            )}
          </Box>
        </GridItem>
      </Grid>

      {/* Group Management Modal */}
      {merchantGroupsHook.isGroupModalOpen && (
        <GroupManagementModal
          isOpen={merchantGroupsHook.isGroupModalOpen}
          onClose={merchantGroupsHook.handleGroupModalCancel}
          groupModalData={merchantGroupsHook.groupModalData}
          setGroupModalData={merchantGroupsHook.setGroupModalData}
          merchantNameList={merchantNameList}
          onSubmit={merchantGroupsHook.handleGroupModalSubmit}
          onCancel={merchantGroupsHook.handleGroupModalCancel}
          onDelete={merchantGroupsHook.handleGroupModalDelete}
          isCreatingGroup={merchantGroupsHook.isCreatingGroup}
          isUpdatingGroup={merchantGroupsHook.isUpdatingGroup}
          isDeleting={merchantGroupsHook.isDeleting}
          onMerchantSelection={merchantGroupsHook.handleGroupMerchantSelection}
        />
      )}
    </>
  );
};

RevenueOverViewGraph.propTypes = {
  revenueOverviewData: PropTypes.any,
  currency: PropTypes.string,
  adminDetails: PropTypes.object,
  handleSelectChange: PropTypes.func.isRequired,
  selectedMerchant: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.object),
  ]),
  merchantNameList: PropTypes.arrayOf(PropTypes.object),
  sellerCommissionData: PropTypes.array,
  isSellerCommissionLoading: PropTypes.bool,
  isRevenueOverviewLoading: PropTypes.bool,
  isErrorLoading: PropTypes.bool,
  showCalendar: PropTypes.bool,
  setShowCalendar: PropTypes.func.isRequired,
  tempDateRange: PropTypes.array,
  dateRange: PropTypes.any,
  calendarFilterTitle: PropTypes.string,
  handleApply: PropTypes.func.isRequired,
  handleClear: PropTypes.func.isRequired,
  handleSelect: PropTypes.func.isRequired,
  selectedRegion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleRegionSelect: PropTypes.func.isRequired,
  showTooltip: PropTypes.bool,
  setShowTooltip: PropTypes.func,
  recurringUsers: PropTypes.any,
  isRecurringLoading: PropTypes.bool,
  showAverage: PropTypes.bool,
  setShowAverage: PropTypes.func,
  powerbankShowAvg: PropTypes.bool,
  setPowerbankShowAvg: PropTypes.func,
};

export default React.memo(RevenueOverViewGraph);
